// 새 채팅 화면의 예시 질문 — 웰컴 카드(qcards)와 컴포저 placeholder 가 같은
// 출처를 쓴다. placeholder 는 '기업 조회'·'기업 발굴' 두 카테고리에서만 뽑는다
// (투자 분석·평가·샘플 질문은 대상 기업/테마가 전제라 첫 입력 예시로 부적절).
// → 새 카테고리는 반드시 앞 두 개 **뒤에** 추가할 것 (PLACEHOLDER_POOL 이 slice(0,2)).

export type HeroChip = string | { label: string; href: string };

/** 전일 하락 종목으로 매일 바뀌는 자리. 서버가 종목을 못 주면 이 문구 그대로 쓴다
 *  (기본 종목은 예시가 성립하는 대형주면 되고, 정확성이 필요한 자리가 아니다). */
export const DECLINER_FALLBACK = "한화에어로스페이스";
export const DECLINER_SLOT = `{company} 요즘 분위기 안 좋던데 무슨 일 있었어?`;

/** 카드 문구의 {company} 를 오늘의 종목으로 채운다. */
export function fillDecliner(chip: HeroChip, name: string): HeroChip {
  if (typeof chip !== "string" || !chip.includes("{company}")) return chip;
  return chip.replace("{company}", name || DECLINER_FALLBACK);
}

export const HERO_CARDS: Array<[string, HeroChip[]]> = [
  [
    "기업 조회",
    [
      "삼성전자 기본정보 보여줘",
      "삼성전자 뉴스·리서치 보고서 알려줘",
      "삼성전자 최근 1년간 주가 알려줘",
      "삼성전자와 코스피 최근 1년 수익률 비교해줘",
      "삼성전자와 SK하이닉스 중 반도체 매출 성장률이 더 높은 곳은 어디야?",
      "효성중공업 기업 리포트 생성해줘",
    ],
  ],
  [
    "기업 발굴",
    [
      "매출 1조원 이상, 영업이익 1000억원 이상 기업 찾아줘",
      "부산 소재 매출액 1,000억 이상 회사 찾아줘",
      "분당구 판교 소재 영업이익 100억 이상 회사 찾아줘",
      // 주거래은행 — V3 bank_name 적재(DB팀 2026-08-21) + 스크리너 2단계 필터로 복구
      "주거래은행이 하나은행이고 영업이익 100억 이상인 기업 찾아줘",
      "상장사 지역별 분포 알려줘",
      "양자컴퓨터 특허 보유 기업 찾아줘",
    ],
  ],
  [
    "투자 분석",
    [
      "유리기판 테마로 실제 매출이 나오는 회사가 있어? 기대감만 있는 곳이랑 구분해줘",
      "요즘 뜨는 피지컬 AI, 관련된 종목들 좀 알려줘",
      "최근 일주일 동안 뉴스가 갑자기 급증한 상장사들 뭐가 있었어?",
      DECLINER_SLOT,
      "최근에 갑자기 안 좋은 얘기 나오는 종목들 있어? 무슨 일인지도 같이 알려줘",
      "바이오 기술이전을 하는 종목들의 각 강점분석",
    ],
  ],
  [
    "가치평가",
    [
      // 전용 페이지가 있는 기능을 위로, 채팅 질문 두 개는 맨 아래로 (사용자 지정)
      { label: "동종업계 peer 골라줘", href: "/peer" },
      { label: "워런 버핏이 그 회사를 보면 뭐라고 할까?", href: "/buffett" },
      { label: "DCF 워크스페이스 (스텝별 + 보고서)", href: "/dcf" },
      "상대가치평가 하고 싶어",
      "삼성전자 WACC 계산해줘 · 베타(β) 구하기",
    ],
  ],
];

/** 기업 발굴 카드의 대체 질문 세트 — 셔플 버튼으로 기본 세트와 토글한다.
 *  전부 ds_query 로 실측 검증된 질문의 자연어 버전 (2026-08 벤치:
 *  매출합계 6s · 매출 2배 성장 11~14s · 3중 복합 38s · top 14s · 공시언급 ~10s · PER/PBR 15s). */
export const DISCOVERY_ALT: HeroChip[] = [
  "삼성전자와 SK하이닉스 매출 합하면 얼마야?",
  "작년에 매출이 2배 성장한 상장사 리스트업 해줘",
  "매출과 영업이익이 모두 2배 이상 성장한 상장사 찾아줘",
  "영업이익률 상위 100개 기업 보여줘",
  "인공지능이 공시에 언급된 기업 찾아줘",
  "삼성전자 최근 3개월 PER, PBR 추이 알려줘",
];

/** placeholder 로 돌릴 예시 질문 — 기업 조회 + 기업 발굴 칩(링크 칩 제외) */
export const PLACEHOLDER_POOL: string[] = HERO_CARDS.slice(0, 2)
  .flatMap(([, chips]) => chips)
  .filter((c): c is string => typeof c === "string");

/** Fisher-Yates 셔플 — 순서를 섞되 한 바퀴 안에서는 중복 없이 돈다 */
export function shuffled<T>(arr: T[], rand: () => number = Math.random): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
